import React, { useCallback, useEffect, useRef, useState } from "react";
import "../styles/Admin.css";

const API_BASE = process.env.NODE_ENV === "production"
    ? "https://main-api.academy-polyglot.site/api/admin"
    : "http://localhost:5001/api/admin";
const IMG_BASE = process.env.NODE_ENV === "production"
    ? "https://main-api.academy-polyglot.site"
    : "http://localhost:5001";

const LANGS = [
    { code: "hy", flag: "🇦🇲", label: "HY" },
    { code: "en", flag: "🇬🇧", label: "EN" },
    { code: "ru", flag: "🇷🇺", label: "RU" },
    { code: "ka", flag: "🇬🇪", label: "KA" },
];

function imgUrl(p) {
    if (!p) return "";
    if (p.startsWith("http")) return p;
    return IMG_BASE + (p.startsWith("/") ? "" : "/") + p;
}

function useApi(token) {
    return useCallback(async (method, path, body, isFormData = false) => {
        const headers = { "x-admin-token": token };
        if (!isFormData) headers["Content-Type"] = "application/json";
        const opts = { method, headers };
        if (body !== undefined) opts.body = isFormData ? body : JSON.stringify(body);
        const r = await fetch(API_BASE + path, opts);
        if (r.status === 401) throw new Error("__UNAUTHORIZED__");
        const text = await r.text();
        let data;
        try { data = JSON.parse(text); } catch { data = text; }
        if (!r.ok) throw new Error(data?.error || "Request failed");
        return data;
    }, [token]);
}

function LangTabs({ active, onChange }) {
    return <div className="adm-lang-tabs">{LANGS.map(l => (
        <button key={l.code} className={`adm-lang-tab${active === l.code ? " active" : ""}`} onClick={() => onChange(l.code)}>
            {l.flag} {l.label}
        </button>
    ))}</div>;
}

function Modal({ title, onClose, onSave, saving, children }) {
    useEffect(() => {
        const h = e => { if (e.key === "Escape") onClose(); };
        window.addEventListener("keydown", h);
        return () => window.removeEventListener("keydown", h);
    }, [onClose]);
    return <div className="adm-modal-backdrop" onClick={e => e.target === e.currentTarget && onClose()}>
        <div className="adm-modal adm-modal-wide">
            <div className="adm-modal-head"><span className="adm-modal-title">{title}</span><button className="adm-modal-close" onClick={onClose}>✕</button></div>
            <div className="adm-modal-body">{children}</div>
            <div className="adm-modal-foot">
                <button className="adm-btn adm-btn-ghost" onClick={onClose}>Cancel</button>
                <button className="adm-btn adm-btn-primary" onClick={onSave} disabled={saving}>{saving ? <span className="adm-spinner" /> : "Save"}</button>
            </div>
        </div>
    </div>;
}

function ImgField({ value, onChange, token, folder = "MyTeam" }) {
    const fileRef = useRef();
    const [uploading, setUploading] = useState(false);
    async function handleFile(e) {
        const file = e.target.files[0];
        if (!file) return;
        setUploading(true);
        try {
            const form = new FormData();
            form.append("image", file);
            const r = await fetch(`${API_BASE}/upload?folder=${folder}`, { method: "POST", headers: { "x-admin-token": token }, body: form });
            const j = await r.json();
            if (j.url) onChange(j.url);
        } finally { setUploading(false); }
    }
    return <div className="adm-f-group adm-full">
        <label className="adm-f-label">Photo</label>
        <div className="adm-img-row">
            <input className="adm-f-input" value={value || ""} onChange={e => onChange(e.target.value)} placeholder="/picture/..." />
            <button className="adm-btn adm-btn-ghost adm-btn-sm" type="button" onClick={() => fileRef.current.click()} disabled={uploading}>{uploading ? <span className="adm-spinner" /> : "📁 Upload"}</button>
        </div>
        <input ref={fileRef} type="file" accept="image/*" style={{ display: "none" }} onChange={handleFile} />
        {value && <img className="adm-img-preview" src={imgUrl(value)} alt="" />}
    </div>;
}


function Teachers({ api, token, notify }) {
    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(true);
    const [modal, setModal] = useState(false);
    const [form, setForm] = useState({});
    const [saving, setSaving] = useState(false);
    const [activeLang, setActiveLang] = useState("hy");
    const load = useCallback(async () => {
        setLoading(true);
        try { setData(await api("GET", "/teachers")); }
        catch (e) { notify(e.message, "err"); }
        finally { setLoading(false); }
    }, [api, notify]);
    useEffect(() => { load(); }, [load]);
    const setF = (k, v) => setForm(p => ({ ...p, [k]: v }));

    async function save() {
        setSaving(true);
        try {
            if (form.id) await api("PUT", `/teachers/${form.id}`, form);
            else await api("POST", "/teachers", form);
            notify("Saved!"); setModal(false); load();
        } catch (e) { notify(e.message || "Save failed", "err"); }
        finally { setSaving(false); }
    }
    async function del(id) {
        if (!window.confirm("Delete this teacher?")) return;
        try { await api("DELETE", `/teachers/${id}`); notify("Deleted"); load(); }
        catch (e) { notify(e.message || "Delete failed", "err"); }
    }

    return <>
        <div className="adm-table-wrap">
            <div className="adm-table-toolbar">
                <strong className="adm-section-title">Teachers</strong><div style={{ flex: 1 }} />
                <button className="adm-btn adm-btn-primary adm-btn-sm" onClick={() => { setForm({}); setActiveLang("hy"); setModal(true); }}>+ Add Teacher</button>
            </div>
            {loading ? <div className="adm-loading"><span className="adm-spinner" /> Loading…</div> :
                <div className="adm-table-scroll"><table className="adm-data-table">
                    <thead><tr><th>ID</th><th>Photo</th><th>Name HY</th><th>Name EN</th><th>Role</th><th>Actions</th></tr></thead>
                    <tbody>{data.length === 0 ? <tr><td colSpan="6" className="adm-empty-cell">No teachers found</td></tr> : data.map(t => <tr key={t.id}>
                        <td><span className="adm-tag adm-tag-blue">{t.id}</span></td>
                        <td>{t.img ? <img className="adm-thumb" src={imgUrl(t.img)} alt="" /> : <span className="adm-thumb-ph">👩‍🏫</span>}</td>
                        <td>{t.name_hy || "—"}</td><td>{t.name_en || "—"}</td><td>{t.role || "—"}</td>
                        <td><div className="adm-actions">
                            <button className="adm-btn adm-btn-ghost adm-btn-sm" onClick={() => { setForm({ ...t }); setActiveLang("hy"); setModal(true); }}>✏️ Edit</button>
                            <button className="adm-btn adm-btn-danger adm-btn-sm" onClick={() => del(t.id)}>🗑</button>
                        </div></td>
                    </tr>)}</tbody>
                </table></div>}
        </div>
        {modal && <Modal title={form.id ? "Edit Teacher" : "New Teacher"} onClose={() => setModal(false)} onSave={save} saving={saving}>
            <LangTabs active={activeLang} onChange={setActiveLang} />
            <div className="adm-form-grid">
                <div className="adm-f-group"><label className="adm-f-label">Name <span className="adm-f-lang">{activeLang}</span></label><input className="adm-f-input" value={form[`name_${activeLang}`] || ""} onChange={e => setF(`name_${activeLang}`, e.target.value)} /></div>
                <div className="adm-f-group adm-full"><label className="adm-f-label">Description <span className="adm-f-lang">{activeLang}</span></label><textarea className="adm-f-textarea" rows="3" value={form[`description_${activeLang}`] || ""} onChange={e => setF(`description_${activeLang}`, e.target.value)} /></div>
                <div className="adm-f-group adm-full"><label className="adm-f-label">Role</label><input className="adm-f-input" value={form.role || ""} onChange={e => setF("role", e.target.value)} /></div>
                <ImgField value={form.img} onChange={v => setF("img", v)} token={token} folder="MyTeam" />
            </div>
        </Modal>}
    </>;
}

function Members({ api, token, notify }) {
    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(true);
    const [modal, setModal] = useState(false);
    const [form, setForm] = useState({});
    const [saving, setSaving] = useState(false);
    const [activeLang, setActiveLang] = useState("hy");
    const load = useCallback(async () => {
        setLoading(true);
        try { setData(await api("GET", "/team")); }
        catch (e) { notify(e.message, "err"); }
        finally { setLoading(false); }
    }, [api, notify]);
    useEffect(() => { load(); }, [load]);
    const setF = (k, v) => setForm(p => ({ ...p, [k]: v }));

    async function save() {
        setSaving(true);
        try {
            if (form.id) await api("PUT", `/team/${form.id}`, form);
            else await api("POST", "/team", form);
            notify("Saved!"); setModal(false); load();
        } catch (e) { notify(e.message || "Save failed", "err"); }
        finally { setSaving(false); }
    }
    async function del(id) {
        if (!window.confirm("Delete this member?")) return;
        try { await api("DELETE", `/team/${id}`); notify("Deleted"); load(); }
        catch (e) { notify(e.message || "Delete failed", "err"); }
    }

    return <>
        <div className="adm-table-wrap">
            <div className="adm-table-toolbar">
                <strong className="adm-section-title">Team Members</strong>
                <div style={{ flex: 1 }} />
                <button className="adm-btn adm-btn-primary adm-btn-sm" onClick={() => { setForm({ status: 1 }); setActiveLang("hy"); setModal(true); }}>+ Add Member</button>
            </div>
            {loading ? <div className="adm-loading"><span className="adm-spinner" /> Loading…</div> :
                <div className="adm-table-scroll"><table className="adm-data-table">
                    <thead><tr><th>ID</th><th>Photo</th><th>Name HY</th><th>Role HY</th><th>Status</th><th>Actions</th></tr></thead>
                    <tbody>{data.length === 0 ? <tr><td colSpan="6" className="adm-empty-cell">No members found</td></tr> : data.map(m => <tr key={m.id}>
                        <td><span className="adm-tag adm-tag-blue">{m.id}</span></td>
                        <td>{m.img ? <img className="adm-thumb" src={imgUrl(m.img)} alt="" /> : <span className="adm-thumb-ph">👤</span>}</td>
                        <td>{m.name_hy || m.name || "—"}</td><td>{m.role_hy || m.role || "—"}</td>
                        <td><span className={`adm-tag ${m.status == 1 ? "adm-tag-green" : "adm-tag-red"}`}>{m.status == 1 ? "active" : "hidden"}</span></td>
                        <td><div className="adm-actions">
                            <button className="adm-btn adm-btn-ghost adm-btn-sm" onClick={() => { setForm({ ...m }); setActiveLang("hy"); setModal(true); }}>✏️ Edit</button>
                            <button className="adm-btn adm-btn-danger adm-btn-sm" onClick={() => del(m.id)}>🗑</button>
                        </div></td>
                    </tr>)}</tbody>
                </table></div>}
        </div>
        {modal && <Modal title={form.id ? "Edit Member" : "New Member"} onClose={() => setModal(false)} onSave={save} saving={saving}>
            <LangTabs active={activeLang} onChange={setActiveLang} />
            <div className="adm-form-grid">
                <div className="adm-f-group"><label className="adm-f-label">Name <span className="adm-f-lang">{activeLang}</span></label><input className="adm-f-input" value={form[`name_${activeLang}`] || ""} onChange={e => setF(`name_${activeLang}`, e.target.value)} /></div>
                <div className="adm-f-group"><label className="adm-f-label">Role <span className="adm-f-lang">{activeLang}</span></label><input className="adm-f-input" value={form[`role_${activeLang}`] || ""} onChange={e => setF(`role_${activeLang}`, e.target.value)} /></div>
                <div className="adm-f-group adm-full"><label className="adm-f-label">Description <span className="adm-f-lang">{activeLang}</span></label><textarea className="adm-f-textarea" rows="3" value={form[`description_${activeLang}`] || ""} onChange={e => setF(`description_${activeLang}`, e.target.value)} /></div>
                <div className="adm-f-group"><label className="adm-f-label">Status</label><select className="adm-f-select" value={form.status ?? 1} onChange={e => setF("status", e.target.value)}><option value={1}>Visible</option><option value={0}>Hidden</option></select></div>
                <ImgField value={form.img} onChange={v => setF("img", v)} token={token} folder="MyTeam" />
            </div>
        </Modal>}
    </>;
}

export default function MembersAdmin() {
    const [token, setToken] = useState(() => localStorage.getItem("members_adm_token") || "");
    const [verified, setVerified] = useState(false);
    const [checking, setChecking] = useState(true);
    const [loginForm, setLoginForm] = useState({ username: "", password: "" });
    const [loginErr, setLoginErr] = useState("");
    const [loggingIn, setLoggingIn] = useState(false);
    const [notice, setNotice] = useState(null);
    const [panel, setPanel] = useState("teachers");
    const api = useApi(token);
    const notify = useCallback((message, type = "ok") => { setNotice({ message, type }); setTimeout(() => setNotice(null), 3000); }, []);

    const logout = useCallback(() => { localStorage.removeItem("members_adm_token"); setToken(""); setVerified(false); }, []);

    useEffect(() => {
        if (!token) { setChecking(false); return; }
        fetch(API_BASE + "/verify", { headers: { "x-admin-token": token } })
            .then(r => r.json()).then(j => { if (j.valid) setVerified(true); else logout(); })
            .catch(logout).finally(() => setChecking(false));
    }, []); // eslint-disable-line

    async function login() {
        setLoggingIn(true); setLoginErr("");
        try {
            const r = await fetch(API_BASE + "/login", { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(loginForm) });
            const j = await r.json();
            if (r.ok && j.token) { localStorage.setItem("members_adm_token", j.token); setToken(j.token); setVerified(true); }
            else setLoginErr(j.error || "Invalid credentials");
        } catch { setLoginErr("Cannot reach server."); }
        finally { setLoggingIn(false); }
    }

    const handleApi = useCallback(async (...args) => {
        try { return await api(...args); }
        catch (e) { if (e.message === "__UNAUTHORIZED__") logout(); throw e; }
    }, [api, logout]);

    if (checking) return <div className="adm-full-center"><span className="adm-spinner adm-spinner-lg" /></div>;
    if (!verified) return <div className="adm-full-center adm-login-bg"><div className="adm-login-card">
        <div className="adm-login-logo">Polyglot <span>Academy</span></div><div className="adm-login-sub">Members Admin</div>
        <div className="adm-field"><label>Username</label><input value={loginForm.username} onChange={e => setLoginForm(p => ({ ...p, username: e.target.value }))} onKeyDown={e => e.key === "Enter" && login()} /></div>
        <div className="adm-field"><label>Password</label><input type="password" value={loginForm.password} onChange={e => setLoginForm(p => ({ ...p, password: e.target.value }))} onKeyDown={e => e.key === "Enter" && login()} /></div>
        <button className="adm-btn adm-btn-primary adm-btn-full" onClick={login} disabled={loggingIn}>{loggingIn ? <span className="adm-spinner" /> : "Sign In"}</button>
        {loginErr && <div className="adm-login-err">{loginErr}</div>}
    </div></div>;

    return <div className="adm-layout">
        <aside className="adm-sidebar"><div className="adm-sidebar-head"><div className="adm-sidebar-logo">Polyglot <span>Members</span></div><div className="adm-sidebar-badge">Members Only</div></div>
            <nav className="adm-sidebar-nav"><div className="adm-nav-section">Content</div>
                <div className={`adm-nav-item${panel === "teachers" ? " active" : ""}`} onClick={() => setPanel("teachers")}><span className="adm-nav-icon">👩‍🏫</span>Teachers</div>
                <div className={`adm-nav-item${panel === "team" ? " active" : ""}`} onClick={() => setPanel("team")}><span className="adm-nav-icon">👥</span>Our Team</div>
            </nav>
            <div className="adm-sidebar-foot"><button className="adm-btn adm-btn-ghost adm-btn-full adm-btn-sm" onClick={logout}>⬅ Sign Out</button></div>
        </aside>
        <div className="adm-main"><div className="adm-topbar"><div className="adm-topbar-title">{panel === "teachers" ? "Teachers" : "Our Team"}</div><div className="adm-topbar-right"><div className="adm-user-chip"><div className="adm-avatar">M</div><span>Members Admin</span></div></div></div>
            <div className="adm-content">{panel === "teachers" ? <Teachers api={handleApi} token={token} notify={notify} /> : <Members api={handleApi} token={token} notify={notify} />}</div></div>
        {notice && <div className="adm-toast-wrap"><div className={`adm-toast adm-toast-${notice.type}`}><span>{notice.type === "ok" ? "✅" : "❌"}</span><span>{notice.message}</span></div></div>}
    </div>;
}
